import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import Img from '../../../components/Img';
import {
    DrawStatusOptions,
    DrawStatusTypes,
    MatchStatusOptions,
    MatchStatusTypes,
    SideBarDisplayTypes,
} from '../../../utils/nextAvailable';
import { clearDrawStatusChangesToNotify, getDrawsConnectedToFacilityAndDayConfig, removeAllDrawsFromFacilityAndDayConfig, setSidebarDisplayAndStatusTypes, setSidebarStatusTypes } from '../../../actions/next-available-actions';

import styles from './nextAvailable.scss';
import MatchContainer from './MatchContainer';
import DrawsContainer from './DrawsContainer';
import { showInfoPopup, showSuccessToast } from '../../../actions/notification-actions';
import { drawCategoryTypeIds } from '../../../utils/eventConstants';

const DrawAndMatchList = (props) => {
    const {
        className,
        onClickAddDraws,
        eventId,
        event,
        onDrawSettingsEdit,
        playersDictByMemberId,
        teamsDict,
        onPostScoreClick,
        openDrawInEditor,
    } = props;

    const dispatch = useDispatch();
    const nextAvailable = useSelector(({ nextAvailable }) => nextAvailable);

    const { sidebar, drawStatusChangesToNotify } = nextAvailable;
    const {
        displayType,
        statusType,
    } = sidebar || {};

    const statusOptions = useMemo(() => {
        if (displayType === SideBarDisplayTypes.DRAWS) {
            return DrawStatusOptions;
        }
        return MatchStatusOptions;
    }, [displayType]);

    const onChangeDisplayType = (type) => () => {
        if (type === SideBarDisplayTypes.DRAWS && displayType !== SideBarDisplayTypes.DRAWS) {
            dispatch(setSidebarDisplayAndStatusTypes(SideBarDisplayTypes.DRAWS, DrawStatusTypes.TODO));
        } else if (type === SideBarDisplayTypes.MATCHES && displayType !== SideBarDisplayTypes.MATCHES) {
            dispatch(setSidebarDisplayAndStatusTypes(SideBarDisplayTypes.MATCHES, MatchStatusTypes.IN_QUEUE));
        }
    };

    const onChangeStatusOption = (id) => () => {
        if (statusType !== id) {
            dispatch(setSidebarStatusTypes(id));
        }
    };

    const onAddDrawsClick = (ev) => {
        if (nextAvailable?.selectedDateCourtConfig && nextAvailable?.selectedFacilityId) {
            onClickAddDraws(ev);
        }
    };

    const onClearAllClick = () => {
        if (nextAvailable?.selectedDateCourtConfig && nextAvailable?.selectedFacilityId) {
            dispatch(removeAllDrawsFromFacilityAndDayConfig(eventId));
        }
    };

    const notifyCompletedDraws = useCallback((drawCompletedNotifications) => {
        drawCompletedNotifications.forEach((notification) => {
            const { drawName, parentDrawName } = notification;
            const name = parentDrawName ? `${parentDrawName} - ${drawName}` : drawName;
            dispatch(showSuccessToast({
                title: 'Success',
                message: `${name} has been completed.`,
            }));
        });
    }, [dispatch]);

    const notifyReadyToStartDraws = useCallback((drawReadyToStartNotifications) => {
        let text = '';
        if (drawReadyToStartNotifications.length === 1) {
            const { drawName, parentDrawName } = drawReadyToStartNotifications[0];
            const name = parentDrawName ? `${parentDrawName} - ${drawName}` : drawName;
            text = `${name} is now ready to start.`;
        } else {
            text = `${drawReadyToStartNotifications.length} draws are now ready to start.`;
        }

        dispatch(showInfoPopup({
            title: 'Ready to Start',
            message: text,
            showCancelButton: false,
            showConfirmButton: false,
            showCloseButton: true,
            noIcon: true,
        }));
    }, [dispatch]);

    const notifyReadyToAutofillDraws = useCallback((drawsToBeAutoFilledNotifications) => {
        if (drawsToBeAutoFilledNotifications.length > 0) {
            const {
                drawId,
                drawCategoryTypeId,
                drawName,
                parentDrawId,
                parentDrawName,
                parentDrawCategoryTypeId,
            } = drawsToBeAutoFilledNotifications[0];

            let text = '';
            let confirmButtonLabel = '';

            if (drawCategoryTypeId === drawCategoryTypeIds.AUTO_PLAYOFF && parentDrawCategoryTypeId === drawCategoryTypeIds.GROUPED) {
                text = `${parentDrawName} has completed their group draws and playoffs are ready to be filled.`;
                confirmButtonLabel = 'Fill Playoff Draw';
            } else if (drawCategoryTypeId === drawCategoryTypeIds.AUTO_PLAYOFF) {
                text = `${parentDrawName} has completed their main draw and playoffs are ready to be filled.`;
                confirmButtonLabel = 'Fill Playoff Draw';
            } else {
                const name = parentDrawName ? `${parentDrawName} - ${drawName}` : drawName;
                text = `${name} is ready to be filled.`;
                confirmButtonLabel = 'Auto Fill Draw';
            }

            const draw = {
                id: drawId,
                categoryTypeId: drawCategoryTypeId,
                name: drawName,
                parentDrawId,
            };

            dispatch(showInfoPopup({
                title: 'Action Required',
                message: text,
                showCancelButton: true,
                cancelButtonLabel: 'Cancel',
                showConfirmButton: true,
                confirmButtonLabel,
                onConfirmClick: () => {
                    openDrawInEditor(draw);
                },
                showCloseButton: true,
                noIcon: true,
            }));
        }
    }, [dispatch]);

    useEffect(() => {
        const {
            drawCompletedNotifications,
            drawReadyToStartNotifications,
            drawsToBeAutoFilledNotifications,
        } = drawStatusChangesToNotify || {};

        const hasCompletedNotifications = drawCompletedNotifications?.length > 0;
        const hasReadyToStartNotifications = drawReadyToStartNotifications?.length > 0;
        const hasAutoFillNotifications = drawsToBeAutoFilledNotifications?.length > 0;

        if (hasCompletedNotifications) {
            notifyCompletedDraws(drawCompletedNotifications);
        }

        if (hasReadyToStartNotifications) {
            notifyReadyToStartDraws(drawReadyToStartNotifications);
        }

        if (hasAutoFillNotifications) {
            notifyReadyToAutofillDraws(drawsToBeAutoFilledNotifications);
        }

        if (hasCompletedNotifications || hasReadyToStartNotifications || hasAutoFillNotifications) {
            dispatch(clearDrawStatusChangesToNotify());
        }
    }, [drawStatusChangesToNotify, notifyReadyToAutofillDraws, notifyReadyToStartDraws, notifyCompletedDraws]);

    useEffect(() => {
        if (eventId &&
            nextAvailable?.selectedDateCourtConfig?.id &&
            nextAvailable?.selectedFacilityId) {
            dispatch(getDrawsConnectedToFacilityAndDayConfig(eventId, true));
        }
    }, [eventId, nextAvailable?.selectedDateCourtConfig?.id, nextAvailable?.selectedFacilityId]);

    return (
        <div className={`${className} ${styles.nextAvailableSideBar}`}>
            <div className={`${styles.header} d-flex align-items-center`}>
                <span className="mr-auto" data-testid="next-available.sidebar.title">Draws & Matches</span>
            </div>
            <div className={`${styles.contentSection}`}>
                <div className={styles.displayType}>
                    <button
                        type="button"
                        className={`btn btn-sm ${styles.optionButton} ${displayType === SideBarDisplayTypes.DRAWS ? styles.selected : ''}`}
                        onClick={onChangeDisplayType(SideBarDisplayTypes.DRAWS)}
                        data-testid="next-available.sidebar.draws-btn"
                    >
                        Draws
                    </button>
                    <button
                        type="button"
                        className={`btn btn-sm ${styles.optionButton} ${displayType === SideBarDisplayTypes.MATCHES ? styles.selected : ''}`}
                        onClick={onChangeDisplayType(SideBarDisplayTypes.MATCHES)}
                        data-testid="next-available.sidebar.matches-btn"
                    >
                        Matches
                    </button>
                </div>
                <div className={styles.statusType}>
                    {statusOptions.map((so) => {
                        return (
                            <button
                                key={`status-option-${displayType}-${so.id}`}
                                type="button"
                                className={`btn btn-sm ${styles.optionButton} ${statusType === so.id ? styles.selected : ''}`}
                                onClick={onChangeStatusOption(so.id)}
                                data-testid={`next-available.sidebar.status-${so.id}-btn`}
                            >
                                {so.label}
                            </button>
                        );
                    })}
                </div>
            </div>
            {displayType === SideBarDisplayTypes.DRAWS && (
                <>
                    {nextAvailable?.selectedDateCourtConfig && statusType === DrawStatusTypes.TODO && (
                        <div className={`${styles.contentSection} d-flex`}>
                            <button
                                className={`btn btn-flex p0 ${styles.addDrawsButton}`}
                                type="button"
                                onClick={onAddDrawsClick}
                                data-testid="next-available.sidebar.add-draws-btn"
                            >
                                <Img className="mr4 mb2" src="/img/v3/icons/icon-18px-circle-plus-teal.svg" /> Add draws
                            </button>
                            <div className="flex-1" />
                            {nextAvailable?.draws.length > 0 && (
                                <button
                                    className={`btn btn-flex p0 ${styles.addDrawsButton} text-hi2`}
                                    type="button"
                                    onClick={onClearAllClick}
                                    data-testid="next-available.sidebar.clear-all-btn"
                                >
                                    Clear all
                                </button>
                            )}
                        </div>
                    )}
                    <DrawsContainer
                        statusType={statusType}
                        className={`${styles.hiddenScroll} flex-1`}
                        event={event}
                        onDrawSettingsEdit={onDrawSettingsEdit}
                    />
                </>
            )}
            {displayType === SideBarDisplayTypes.MATCHES && (
                <MatchContainer
                    eventId={eventId}
                    sportTypeId={event?.sportTypeId}
                    displayType={displayType}
                    statusType={statusType}
                    className={`${styles.contentSection} ${styles.hiddenScroll} flex-1`}
                    playersDictByMemberId={playersDictByMemberId}
                    teamsDict={teamsDict}
                    eventSchedule={event?.eventSchedule}
                    onPostScoreClick={onPostScoreClick}
                />
            )}
        </div>
    );
};

DrawAndMatchList.propTypes = {
    eventId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    className: PropTypes.string,
    onClickAddDraws: PropTypes.func.isRequired,
    onDrawSettingsEdit: PropTypes.func.isRequired,
    event: PropTypes.objectOf(PropTypes.any).isRequired,
    playersDictByMemberId: PropTypes.objectOf(PropTypes.any).isRequired,
    teamsDict: PropTypes.objectOf(PropTypes.any).isRequired,
    onPostScoreClick: PropTypes.func.isRequired,
    openDrawInEditor: PropTypes.func.isRequired,
};

DrawAndMatchList.defaultProps = {
    className: null,
};

export default DrawAndMatchList;
